import { useMergeContext } from '../../../store/mergeContext';
import { useLingui } from '@lingui/react';
import { useTheme } from '@emotion/react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress } from
'@mui/material';
import { BASIC_ORG } from '../merge-constants';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, msg } from '@lingui/macro';
import {
  COLLECTED_BY_ID,
  CRASH_ID_KEY,
  DATE_ID_KEY,
  getColumnsModel,
  getOrganization } from
'../utils';
import { getConfigOptions, mapObjectToLabels } from '../../Statistics/utils';
import { formatDateForList } from '../../../utils';
import UserControls from '../../../components/desktop/UserControls';
import { StripedDataGrid } from "../../../components/tables/StripedDataGrid";
import DesktopToolbar from "../../../components/desktop/DesktopToolbar";


const SOURCE_OPTIONS = [
{ basic: BASIC_ORG.POLICE, msgKey: 'police' },
{ basic: BASIC_ORG.HOSPITAL, msgKey: 'hospital' },
{ basic: BASIC_ORG.INSURANCE, msgKey: 'insurance' }];


export const CrashTable = ({ isLoadingCrashes = false }) => {
  const { _ } = useLingui();
  const {
    crashes,
    handleSelectedCrash,
    mergedCrashes,
    sourceBasicOrganization,
    setSourceBasicOrganization
  } = useMergeContext();

  const [columns, setColumns] = useState([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState([]);
  const [rows, setRows] = useState([]);
  const [validatedRow, setValidatedRows] = useState([]);
  const [user, setUser] = useState({});
  const location = useLocation();
  const state = location.state;
  const theme = useTheme();
  const init = useMemo(
    () => location.state?.init || {},
    [location.state?.init]
  );
  const organizations = useMemo(
    () => location.state?.init?.organizations || [],
    [location.state?.init?.organizations]
  );

  const sourceOrgLabels = useMemo(
    () => ({
      [BASIC_ORG.POLICE]: _(msg`Police`),
      [BASIC_ORG.HOSPITAL]: _(msg`Hospital`),
      [BASIC_ORG.INSURANCE]: _(msg`Insurance`)
    }),
    [_]
  );
  const dateFormatter = useCallback(
    (paramsOrValue) => formatDateForList(paramsOrValue?.value ?? paramsOrValue),
    []
  );

  const columnsWithDateFormatter = useMemo(
    () =>
    columns.map((col) =>
    col.field === DATE_ID_KEY || /\bdate\b/i.test(col.field ?? '') ?
    { ...col, valueFormatter: dateFormatter } :
    col
    ),
    [columns, dateFormatter]
  );

  const validatedColumns = useMemo(
    () => [
    ...columnsWithDateFormatter,
    {
      field: 'merge_date',
      headerName: _(msg`Validation date`),
      headerClassName: 'overload-header',
      rowSpanValueGetter: () => null,
      flex: 1,
      valueFormatter: dateFormatter
    }],

    [columnsWithDateFormatter, _, dateFormatter]
  );


  useEffect(() => {
    const { configuration } = init;

    const configOptions = getConfigOptions(configuration);

    const mappedCrashes = (mergedCrashes ?? []).map((crash) =>
    mapObjectToLabels(crash, configOptions)
    );

    const preparedRows = mappedCrashes.map((crash) => ({
      ...crash.crash,
      units: Object.entries(crash?.units)?.length,
      people:
        Object.values(crash?.units)?.flatMap((v) =>
          Object.values(v?.people)?.flatMap((p) => p)
        )?.length + Object.values(crash?.people || {})?.length,
      [COLLECTED_BY_ID]: getOrganization(
        crash?.crash?.[COLLECTED_BY_ID],
        organizations
      )
    }));


    setValidatedRows(
      preparedRows?.map((row, index) => ({ ...row, id: index }))
    );
  }, [init, mergedCrashes, organizations]);

  useEffect(() => {
    const { configuration, user } = init;
    setUser(user);

    const configOptions = getConfigOptions(configuration);

    const mappedCrashes = (crashes ?? []).map((crash) =>
    mapObjectToLabels(crash, configOptions)
    );

    const preparedRows = mappedCrashes.map((crash) => ({
      ...crash.crash,
      units: Object.entries(crash?.units)?.length,
      people:
        Object.values(crash?.units)?.flatMap((v) =>
          Object.values(v?.people)?.flatMap((p) => p)
        )?.length + Object.values(crash?.people || {})?.length,
      [COLLECTED_BY_ID]: getOrganization(
        crash?.crash?.[COLLECTED_BY_ID],
        organizations
      )
    }));

    setRows(preparedRows?.map((row, index) => ({ ...row, id: index })));

    const { columns } = getColumnsModel(configuration);

    setColumnGroupingModel();

    setColumns(columns);
  }, [init, crashes, organizations]);

  return (
    <Stack direction="column">
      <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.newColors.gray}` }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray, fontWeight: 'bold', fontSize: 24 }}>{_(msg`Data merging and validation`)}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 1, padding: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontWeight: 'bold', fontSize: 18, ml: -2 }} variant="h3">
          <Trans>SELECT A CRASH TO VALIDATE</Trans>
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontSize: 14 }}>
            <Trans>Source organization</Trans>
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={sourceBasicOrganization}
            disabled={isLoadingCrashes}
            onChange={(_e, value) => {
              if (value != null) setSourceBasicOrganization(value);
            }}>
            
            {SOURCE_OPTIONS.map(({ basic }) =>
            <ToggleButton key={basic} value={basic}>
                {sourceOrgLabels[basic]}
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Paper sx={{ mb: 2, position: 'relative', minHeight: 200 }}>
        {isLoadingCrashes &&
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) =>
            t.palette.mode === 'light' ?
            'rgba(255, 255, 255, 0.72)' :
            'rgba(0, 0, 0, 0.45)'
          }}
          aria-busy="true"
          aria-live="polite">
          
            <CircularProgress size={40} />
          </Box>
        }
        <StripedDataGrid
          rowSelection
          onRowClick={(e) => !isLoadingCrashes && handleSelectedCrash(e?.row?.[CRASH_ID_KEY])}
          showColumnVerticalBorder
          showCellVerticalBorder
          rows={rows}
          columns={columnsWithDateFormatter}
          editMode="row"
          columnGroupingModel={columnGroupingModel}
          pageSizeOptions={[5]}
          sx={{
            border: 0,
            opacity: isLoadingCrashes ? 0.5 : 1,
            pointerEvents: isLoadingCrashes ? 'none' : 'auto',
            '& .MuiDataGrid-menuIcon > *': {
              color: theme.palette.primary.contrastText
            }
          }} />
        
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 1, padding: 2 }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontWeight: 'bold', fontSize: 18, ml: -2 }} variant="h3">
          <Trans>VALIDATED CRASHES</Trans>
        </Typography>

      </Box>
      <Divider sx={{ mb: 2 }} />
      <Paper sx={{ pt: -1, mb: 2 }}>
        <StripedDataGrid
          rowSelection={false}
          showColumnVerticalBorder
          showCellVerticalBorder
          rows={validatedRow}
          columns={validatedColumns}
          editMode="row"
          columnGroupingModel={columnGroupingModel}
          pageSizeOptions={[5]}
          sx={{
            border: 0,
            '& .MuiDataGrid-menuIcon > *': {
              color: theme.palette.primary.contrastText
            }
          }} />
        
      </Paper>
    </Stack>);

};